import { useState } from 'react'

import { LocalStorageKeys, SetStateFn } from '~config/constants'
import { ChatPreviewFieldsFragment } from '~graphql/generated/graphql'
import { useLocalStorage } from '~utils/hooks/use-local-storage'

/**
 * Which messages a chat preview card's text is drawn from. Each option maps
 * 1:1 to a denormalized "last message" column the server maintains on the
 * chat (see `updateChatOnNewMessage` in the server's message-helpers).
 */
export enum ChatPreviewMessageSource {
    /** Latest client-visible message, including system/automated messages. */
    All = 'All',
    /** Latest message written by the client or clinic staff (no system messages). */
    ClientAndClinic = 'ClientAndClinic',
    /** Latest message written by the client. */
    ClientOnly = 'ClientOnly',
}

export const DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE = ChatPreviewMessageSource.All

export const CHAT_PREVIEW_MESSAGE_SOURCE_LABELS: Record<
    ChatPreviewMessageSource,
    string
> = {
    [ChatPreviewMessageSource.All]: 'All messages',
    [ChatPreviewMessageSource.ClientAndClinic]: 'Client + clinic',
    [ChatPreviewMessageSource.ClientOnly]: 'Client only',
}

export interface ChatPreviewMessage {
    text: string | null
    /** True when `text` is an internal note the client never saw. */
    isInternalNote: boolean
}

type ChatPreviewMessageFields = Pick<
    ChatPreviewFieldsFragment,
    | 'last_message'
    | 'client_last_message'
    | 'non_system_last_message'
    | 'non_system_last_message_time'
    | 'system_last_message_time'
>

/**
 * Persists the user's preview message source under `storageKey`. Users who
 * had the legacy "Only show client messages" toggle on start on
 * {@link ChatPreviewMessageSource.ClientOnly} so their view doesn't change.
 */
export function useChatPreviewMessageSource(
    storageKey: LocalStorageKeys,
    legacyShowClientMsgsOnlyKey: LocalStorageKeys,
): [ChatPreviewMessageSource, SetStateFn<ChatPreviewMessageSource>] {
    const [initialSource] = useState(() =>
        getSourceFromLegacyClientOnlyToggle(legacyShowClientMsgsOnlyKey),
    )
    const [storedSource, setStoredSource] =
        useLocalStorage<ChatPreviewMessageSource>(storageKey, initialSource)

    const messageSource = isChatPreviewMessageSource(storedSource)
        ? storedSource
        : DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE

    return [messageSource, setStoredSource]
}

/**
 * Resolves the text a chat preview card should show for `messageSource`.
 *
 * @param showInternalNotes - When the source is
 * {@link ChatPreviewMessageSource.All}, surface a newer internal note instead
 * of `last_message` (which internal notes never update). Notes are always
 * surfaced for {@link ChatPreviewMessageSource.ClientAndClinic}, since
 * `non_system_last_message` includes them.
 */
export function getChatPreviewMessage(
    chat: ChatPreviewMessageFields,
    messageSource: ChatPreviewMessageSource,
    showInternalNotes: boolean,
): ChatPreviewMessage {
    switch (messageSource) {
        case ChatPreviewMessageSource.ClientOnly:
            return {
                text:
                    chat.client_last_message ||
                    'No messages from this client yet...',
                isInternalNote: false,
            }
        case ChatPreviewMessageSource.ClientAndClinic:
            return {
                text:
                    chat.non_system_last_message ||
                    'No messages from the client or clinic yet...',
                isInternalNote: isLatestNonSystemMessageInternalNote(chat),
            }
        case ChatPreviewMessageSource.All: {
            const isInternalNote =
                showInternalNotes && isLatestNonSystemMessageInternalNote(chat)

            return {
                text: isInternalNote
                    ? chat.non_system_last_message ?? null
                    : chat.last_message ?? null,
                isInternalNote,
            }
        }
    }
}

export function isChatPreviewMessageSource(
    value: unknown,
): value is ChatPreviewMessageSource {
    return Object.values<unknown>(ChatPreviewMessageSource).includes(value)
}

/**
 * Internal notes (`hidden_for_client: true`) update `non_system_last_message`
 * but not `last_message`, so a text mismatch suggests a note. A system
 * message also causes a mismatch (it updates `last_message` only), so we
 * additionally require the non-system message to be the newer of the two.
 *
 * Known gap: a note followed by a system message reads as a regular message,
 * because the chat row doesn't record whether its latest non-system message
 * was hidden from the client.
 */
function isLatestNonSystemMessageInternalNote(
    chat: ChatPreviewMessageFields,
): boolean {
    return (
        !!chat.non_system_last_message &&
        chat.non_system_last_message !== chat.last_message &&
        chat.non_system_last_message_time >= chat.system_last_message_time
    )
}

function getSourceFromLegacyClientOnlyToggle(
    legacyKey: LocalStorageKeys,
): ChatPreviewMessageSource {
    if (typeof window === 'undefined') {
        return DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE
    }

    try {
        const storedValue = window.localStorage.getItem(legacyKey)
        const wasClientOnly: unknown = storedValue
            ? JSON.parse(storedValue)
            : false

        return wasClientOnly === true
            ? ChatPreviewMessageSource.ClientOnly
            : DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE
    } catch (error) {
        console.error(
            `Failed to read legacy localStorage key "${legacyKey}" while migrating the chat preview message source; falling back to "${DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE}":`,
            error,
        )
        return DEFAULT_CHAT_PREVIEW_MESSAGE_SOURCE
    }
}
